import { useState, useEffect, useCallback } from 'react'
import { Crown, Shield, User, Trash2 } from 'lucide-react'
import { usersApi, WorkspaceUser, type ApiResponse } from '../services/api'
import { workspaceService } from '../services/workspaceService'
import { useWorkspaceStore } from '../stores/workspaceStore'
import {
  canAccessFeature,
  canChangeMemberRoles,
  canRemoveMembers,
  invitableRoles,
  isStaleRoleRefusal,
} from '../utils/permissions'
import { useWorkspaceRole } from '../utils/useWorkspaceRole'
import { serverSentence } from '../utils/refusal'
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  DataTable,
  EmptyState,
  Field,
  IconButton,
  Inline,
  LoadingState,
  Modal,
  RadioGroup,
  SettingRow,
  Stack,
} from '../ds'

/**
 * The server's refusal as an Error — and, when it says the caller's role here may not be what
 * this screen believed (`isStaleRoleRefusal`), a membership re-read, so the screen stops
 * offering acts the server will keep refusing. The refusal deletes nothing itself: the re-read
 * decides (PR #237 round 7 — an admin refused an owner-only act keeps admin). It is still
 * thrown, so the modal shows it in the server's words — `serverSentence`, the one rule Limits
 * uses too (`message`, then `error`, then the fallback; PR #237 round 4).
 */
function refusal(response: ApiResponse, fallback: string, asked: IssuedBy): Error {
  if (isStaleRoleRefusal(response)) {
    void workspaceService.refreshMembership({ refusedClerkUserId: asked.clerkUserId })
  }
  return new Error(serverSentence(response, fallback))
}

/**
 * Who a request was issued as, captured BEFORE it is sent. A refusal is about that user in
 * that workspace; if the signed-in user changed while it was in flight, `refreshMembership`
 * leaves the new user's rows alone.
 */
interface IssuedBy {
  clerkUserId: string | undefined
}

type Role = 'owner' | 'admin' | 'member'

const ROLE_NAME: Record<Role, string> = { owner: 'Owner', admin: 'Admin', member: 'Member' }

/** A role option's label: its icon and its name. */
function roleLabel(role: Role) {
  return (
    <Inline>
      {role === 'owner' && <Crown size={16} aria-hidden="true" />}
      {role === 'admin' && <Shield size={16} aria-hidden="true" />}
      {role === 'member' && <User size={16} aria-hidden="true" />}
      <span>{ROLE_NAME[role]}</span>
    </Inline>
  )
}

interface EditRoleModalProps {
  user: WorkspaceUser
  onClose: () => void
  onSave: (userId: string, role: Role) => Promise<void>
  isLastOwner: boolean
}

const EditRoleModal = ({ user, onClose, onSave, isLastOwner }: EditRoleModalProps) => {
  const [selectedRole, setSelectedRole] = useState<Role>(user.role)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async () => {
    if (selectedRole === user.role) {
      onClose()
      return
    }

    setSaving(true)
    setError(null)
    try {
      await onSave(user.id, selectedRole)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open
      onClose={saving ? undefined : onClose}
      dismissible={!saving}
      title="Edit Role"
      description={`Change role for ${user.name || user.email}`}
      actions={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button busy={saving} busyLabel="Saving…" disabled={selectedRole === user.role} onClick={handleSave}>
            Save
          </Button>
        </>
      }
    >
      <Stack>
        {error && (
          <Alert tone="danger" live="assertive">
            {error}
          </Alert>
        )}
        <RadioGroup
          legend="Role"
          value={selectedRole}
          onChange={(v) => setSelectedRole(v as Role)}
          options={(['owner', 'admin', 'member'] as Role[]).map((role) => {
            const isDisabled = role !== 'owner' && user.role === 'owner' && isLastOwner
            return {
              value: role,
              label: roleLabel(role),
              disabled: isDisabled,
              description: isDisabled ? 'Cannot demote last owner' : undefined,
            }
          })}
        />
      </Stack>
    </Modal>
  )
}

interface InviteModalProps {
  onClose: () => void
  onInvite: (email: string, role: Role) => Promise<void>
  /** Only the roles the server will let this caller grant (admins cannot invite owners). */
  roles: Role[]
}

const InviteModal = ({ onClose, onInvite, roles }: InviteModalProps) => {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Role>('member')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleInvite = async () => {
    if (!email.trim()) {
      setError('Email is required')
      return
    }

    setSending(true)
    setError(null)
    try {
      await onInvite(email.trim(), role)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation')
    } finally {
      setSending(false)
    }
  }

  return (
    <Modal
      open
      onClose={sending ? undefined : onClose}
      dismissible={!sending}
      title="Invite Team Member"
      actions={
        <>
          <Button variant="ghost" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button busy={sending} busyLabel="Sending…" disabled={!email.trim()} onClick={handleInvite}>
            Send Invitation
          </Button>
        </>
      }
    >
      <Stack>
        {error && (
          <Alert tone="danger" live="assertive">
            {error}
          </Alert>
        )}
        <Field
          label="Email Address"
          type="email"
          value={email}
          onChange={(e) => setEmail((e.target as HTMLInputElement).value)}
          placeholder="colleague@company.com"
        />
        <RadioGroup
          legend="Role"
          value={role}
          onChange={(v) => setRole(v as Role)}
          options={roles.map((r) => ({ value: r, label: roleLabel(r) }))}
        />
      </Stack>
    </Modal>
  )
}

interface RemoveConfirmModalProps {
  user: WorkspaceUser
  /** Where they are being removed from — the standard wording names it. */
  storeName: string
  onClose: () => void
  onConfirm: () => Promise<void>
}

const RemoveConfirmModal = ({ user, storeName, onClose, onConfirm }: RemoveConfirmModalProps) => {
  const [removing, setRemoving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const name = user.name || user.email

  const handleConfirm = async () => {
    setRemoving(true)
    setError(null)
    try {
      await onConfirm()
      onClose()
    } catch (err) {
      // Show the server's refusal (403 not-an-owner, 409 last-owner / changed underneath us)
      // rather than silently re-enabling the button.
      setError(err instanceof Error ? err.message : 'Failed to remove user')
      setRemoving(false)
    }
  }

  // ConfirmDialog standard wording: "Remove {name} from {store}?" · Keep {name} · Remove {name}.
  return (
    <ConfirmDialog
      open
      title={`Remove ${name} from ${storeName}?`}
      description="They will lose access immediately."
      cancelLabel={`Keep ${name}`}
      confirmLabel={`Remove ${name}`}
      onCancel={onClose}
      onConfirm={handleConfirm}
      busy={removing}
      error={error ?? undefined}
    />
  )
}

interface EditProfileModalProps {
  user: WorkspaceUser
  onClose: () => void
  onSave: (userId: string, firstName: string, lastName: string) => Promise<void>
}

const EditProfileModal = ({ user, onClose, onSave }: EditProfileModalProps) => {
  const [firstName, setFirstName] = useState(user.firstName || '')
  const [lastName, setLastName] = useState(user.lastName || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError('First name and last name are required')
      return
    }

    setSaving(true)
    setError(null)
    try {
      await onSave(user.id, firstName.trim(), lastName.trim())
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open
      onClose={saving ? undefined : onClose}
      dismissible={!saving}
      title="Edit Profile"
      actions={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            busy={saving}
            busyLabel="Saving…"
            disabled={!firstName.trim() || !lastName.trim()}
            onClick={handleSave}
          >
            Save
          </Button>
        </>
      }
    >
      <Stack>
        {error && (
          <Alert tone="danger" live="assertive">
            {error}
          </Alert>
        )}
        <Field
          label="First Name"
          value={firstName}
          onChange={(e) => setFirstName((e.target as HTMLInputElement).value)}
          placeholder="Enter your first name"
        />
        <Field
          label="Last Name"
          value={lastName}
          onChange={(e) => setLastName((e.target as HTMLInputElement).value)}
          placeholder="Enter your last name"
        />
      </Stack>
    </Modal>
  )
}

const TeamView = () => {
  const { currentWorkspace, currentAccount, currentUser } = useWorkspaceStore()
  const workspaceId = currentWorkspace?.id
  // Read at the moment a request is issued, not from this render's closure.
  const issuedBy = useCallback((): IssuedBy => ({
    clerkUserId: useWorkspaceStore.getState().userMembership?.clerkUserId,
  }), [])
  const [users, setUsers] = useState<WorkspaceUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Modal states
  const [editingUser, setEditingUser] = useState<WorkspaceUser | null>(null)
  const [editingProfile, setEditingProfile] = useState<WorkspaceUser | null>(null)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [removingUser, setRemovingUser] = useState<WorkspaceUser | null>(null)

  // The caller's role in THIS workspace, from the ONE role hook (PR #237 round 5) — the same
  // source as the Account gate that mounts this view. See `roleForWorkspace` for why no row
  // means no role and how the post-create window is covered without a `currentUser` fallback.
  const { role: callerRole } = useWorkspaceRole()

  // Check if user has access to team view
  const hasAccess = canAccessFeature(callerRole, 'account')

  // MAR-250: what the Users API will accept from this caller. Owners and admins may invite
  // (admins only admin/member); only owners change roles or remove other members.
  const inviteRoles: Role[] = invitableRoles(callerRole)
  const canInvite = inviteRoles.length > 0
  const canEditRoles = canChangeMemberRoles(callerRole)
  const canRemoveOthers = canRemoveMembers(callerRole)

  const loadUsers = useCallback(async () => {
    // A caller the page will refuse never needs the roster.
    if (!workspaceId || !hasAccess) return

    setLoading(true)
    setError(null)
    const asked = issuedBy()
    try {
      const response = await usersApi.listByWorkspace(workspaceId)
      if (response.success && response.data) {
        setUsers(response.data)
      } else {
        setError(refusal(response, 'Failed to load team members', asked).message)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load team members')
    } finally {
      setLoading(false)
    }
  }, [workspaceId, hasAccess, issuedBy])

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  // Count owners to prevent demoting the last one
  const ownerCount = users.filter((u) => u.role === 'owner').length

  // Show access denied if user doesn't have permission
  if (!hasAccess) {
    return (
      <EmptyState title="Access Denied">
        You don't have permission to access the Team page. Contact your workspace owner or admin for access.
      </EmptyState>
    )
  }

  const handleUpdateRole = async (userId: string, role: Role) => {
    const asked = issuedBy()
    const response = await usersApi.updateRole(userId, role)
    if (!response.success) {
      throw refusal(response, 'Failed to update role', asked)
    }
    await loadUsers()
  }

  const handleInvite = async (email: string, role: Role) => {
    if (!currentAccount?.id || !currentWorkspace?.id) {
      throw new Error('No workspace selected')
    }

    const asked = issuedBy()
    const response = await usersApi.invite({
      email,
      role,
      accountId: currentAccount.id,
      workspaceId: currentWorkspace.id,
    })

    if (!response.success) {
      throw refusal(response, 'Failed to send invitation', asked)
    }
    await loadUsers()
  }

  const handleRemove = async (userId: string) => {
    const asked = issuedBy()
    const response = await usersApi.remove(userId)
    if (!response.success) {
      throw refusal(response, 'Failed to remove user', asked)
    }
    // Removing one of your OWN rows is leaving: the role it carried is gone, so the membership
    // is re-read (without that row) and this screen re-renders on what is left — usually
    // Access Denied. Re-reading the roster would only be refused.
    if (workspaceId && (await workspaceService.afterMemberRemoved(workspaceId, userId))) return
    await loadUsers()
  }

  const handleUpdateProfile = async (userId: string, firstName: string, lastName: string) => {
    const asked = issuedBy()
    const response = await usersApi.updateProfile(userId, {
      firstName,
      lastName,
      name: `${firstName} ${lastName}`
    })
    if (!response.success) {
      throw refusal(response, 'Failed to update profile', asked)
    }
    await loadUsers()
  }

  const getInitials = (user: WorkspaceUser) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    }
    if (user.name) {
      const parts = user.name.split(' ')
      return parts.length > 1
        ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
        : user.name.substring(0, 2).toUpperCase()
    }
    return user.email.substring(0, 2).toUpperCase()
  }

  const getDisplayName = (user: WorkspaceUser) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`
    }
    return user.name || user.email
  }

  const memberColumns = [
    {
      key: 'member',
      label: 'Member',
      render: (member: WorkspaceUser) => {
        const isCurrentUser = member.id === currentUser?.id
        return (
          <Inline>
            <Avatar name={getInitials(member)} size="sm" label={false} />
            <div>
              <div>
                {getDisplayName(member)}
                {isCurrentUser && <span className="t-hint"> (you)</span>}
              </div>
              <div className="t-small">
                {member.email}
                {member.status === 'pending' && ' · invitation pending'}
              </div>
            </div>
          </Inline>
        )
      },
    },
    {
      key: 'role',
      label: 'Role',
      render: (member: WorkspaceUser) => (
        // Worded for display ("Owner", not the stored "owner"); an unknown value still shows as stored.
        <Badge tone={member.role === 'owner' ? 'gold' : member.role === 'admin' ? 'sage' : 'neutral'}>{ROLE_NAME[member.role as Role] ?? member.role}</Badge>
      ),
    },
    {
      key: 'actions',
      label: <span className="mv-sr">Actions</span>,
      align: 'right' as const,
      render: (member: WorkspaceUser) => {
        const isCurrentUser = member.id === currentUser?.id
        const isLastOwner = member.role === 'owner' && ownerCount <= 1
        const canEditRole = canEditRoles && !isCurrentUser
        const canRemove = canRemoveOthers && !isCurrentUser && !isLastOwner
        return (
          <Inline justify="end">
            {isCurrentUser && (
              <Button variant="ghost" size="sm" onClick={() => setEditingProfile(member)}>
                Edit profile
              </Button>
            )}
            {canEditRole && (
              <Button variant="ghost" size="sm" onClick={() => setEditingUser(member)}>
                Edit role
              </Button>
            )}
            {canRemove && (
              <IconButton
                icon={<Trash2 size={14} />}
                label={`Remove ${getDisplayName(member)}…`}
                title="Remove member"
                tone="danger"
                size="sm"
                onClick={() => setRemovingUser(member)}
              />
            )}
          </Inline>
        )
      },
    },
  ]

  return (
    <Stack>
      {/* Members */}
      <Card>
        <CardHeader
          title="Members"
          description="Who can see Marva and change how she works."
          aside={
            canInvite && (
              <Button size="sm" onClick={() => setShowInviteModal(true)}>
                Invite
              </Button>
            )
          }
        />

        {loading ? (
          <LoadingState caption="Loading team…" />
        ) : error ? (
          <Alert
            tone="danger"
            actions={
              <Button variant="ghost" size="sm" onClick={loadUsers}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        ) : users.length === 0 ? (
          <EmptyState size="sm">
            No team members yet.{canInvite ? ' Invite your first teammate above.' : ''}
          </EmptyState>
        ) : (
          <DataTable caption="Team members" columns={memberColumns} rows={users} stickyFirst={false} />
        )}
      </Card>

      {/* Roles */}
      <Card>
        <CardHeader title="Roles" />
        <SettingRow title="Owner" description="Full access, including billing and team." />
        <SettingRow title="Admin" description="Everything except billing — can change how Marva works." />
        <SettingRow title="Member" description="Read the journal and memory; can’t change guardrails." />
      </Card>

      {/* Modals */}
      {editingUser && (
        <EditRoleModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={handleUpdateRole}
          isLastOwner={editingUser.role === 'owner' && ownerCount <= 1}
        />
      )}

      {showInviteModal && (
        <InviteModal onClose={() => setShowInviteModal(false)} onInvite={handleInvite} roles={inviteRoles} />
      )}

      {removingUser && (
        <RemoveConfirmModal
          user={removingUser}
          storeName={currentWorkspace?.name || 'this workspace'}
          onClose={() => setRemovingUser(null)}
          onConfirm={() => handleRemove(removingUser.id)}
        />
      )}

      {editingProfile && (
        <EditProfileModal
          user={editingProfile}
          onClose={() => setEditingProfile(null)}
          onSave={handleUpdateProfile}
        />
      )}
    </Stack>
  )
}

export default TeamView
